import React, { useRef, useState } from 'react';
import { ElevatedButton } from '@producer/ui-kit';
import { MixerHorizontalIcon } from '@radix-ui/react-icons';

import { DrilldownMenu } from '../../../components/drilldown-menu/drilldown-menu';
import { CrewFilterChip } from './crew-filter-chip';

import type { CheckboxItem } from '../../../components/modals/dropdown-menu/DropdownMenu';
import type { CrewFilter } from '../lib/filter-crew';

import styles from './crew-filters.module.scss';

type CrewFiltersProps = {
  value: CrewFilter;
  onChange: (next: CrewFilter) => void;
  departments: string[];
  teams: string[];
};

type CrewFilterCategory = 'department' | 'team';

const CATEGORY_TITLES: Record<CrewFilterCategory, string> = {
  department: 'Department',
  team: 'Team',
};

// Mirrors `ProjectTasksFilters` (apps/webapp/src/features/tasks/components/project-tasks-filters):
// a `DrilldownMenu` whose trigger is the row of active-filter chips plus the "Filter" button.
// Unlike the task filter, this component owns no MobX model — the caller's own `useState` is the
// source of truth, which is what keeps `CrewExportMenu` honest about what is on screen.
export const CrewFilters: React.FC<CrewFiltersProps> = ({
  value,
  onChange,
  departments,
  teams,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [alignOffset, setAlignOffset] = useState(0);
  const [defaultMenuPath, setDefaultMenuPath] = useState<number[]>([]);

  const toggle = (category: CrewFilterCategory, option: string, isChecked: boolean) => {
    const current = value[category] ?? [];
    const next = isChecked ? [...current, option] : current.filter((item) => item !== option);

    onChange({ ...value, [category]: next });
  };

  const buildItems = (category: CrewFilterCategory, options: string[]): CheckboxItem[] =>
    options.map((option) => ({
      title: option,
      isChecked: value[category]?.includes(option),
      onCheckedChange: (isChecked: boolean) => toggle(category, option, isChecked),
      // Keeps the drilldown open after each toggle, so several boxes can be checked in a row.
      onSelect: (e: Event) => e.preventDefault(),
    }));

  const options: React.ComponentProps<typeof DrilldownMenu>['items'] = [
    {
      menuKey: 'department',
      title: CATEGORY_TITLES.department,
      subItems: buildItems('department', departments),
      onSelect: (e) => e.preventDefault(),
    },
    {
      menuKey: 'team',
      title: CATEGORY_TITLES.team,
      subItems: buildItems('team', teams),
      onSelect: (e) => e.preventDefault(),
    },
  ];

  const activeCategories = (['department', 'team'] as const).filter(
    (category) => (value[category]?.length ?? 0) > 0,
  );

  const presetFilterTrigger = (event: React.MouseEvent<HTMLDivElement>, token?: string) => {
    if (!triggerRef.current) {
      return;
    }

    const div = event.currentTarget;
    const offset = Math.ceil(div.offsetLeft - triggerRef.current.offsetLeft);

    setAlignOffset(offset);

    if (!token) {
      setDefaultMenuPath([]);
      return;
    }

    const menuPath = options.findIndex((option) => option.menuKey === token);
    setDefaultMenuPath([menuPath]);
  };

  return (
    <DrilldownMenu
      items={options}
      align="start"
      alignOffset={alignOffset}
      defaultMenuPath={defaultMenuPath}
      trigger={
        <div className={styles['container']} ref={triggerRef}>
          {activeCategories.map((category) => (
            <div
              key={category}
              onPointerEnter={(e) => presetFilterTrigger(e, category)}
              onClick={(e) => presetFilterTrigger(e, category)}
            >
              <CrewFilterChip
                title={CATEGORY_TITLES[category]}
                values={value[category] ?? []}
                onClear={() => onChange({ ...value, [category]: [] })}
              />
            </div>
          ))}

          <div
            onPointerEnter={(e) => presetFilterTrigger(e)}
            onClick={(e) => presetFilterTrigger(e)}
          >
            <ElevatedButton
              icon={<MixerHorizontalIcon />}
              text={activeCategories.length ? '' : 'Filter'}
            />
          </div>
        </div>
      }
      modal
    />
  );
};
